import type {
  ChallengeNameType,
  InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  AuthFlowType,
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  InternalErrorException,
  NotAuthorizedException,
  RespondToAuthChallengeCommand,
  RevokeTokenCommand,
  UserNotFoundException,
} from '@aws-sdk/client-cognito-identity-provider';
import type { ResetPasswordRequest } from 'services/generated/auth-service/models/ResetPasswordRequest';
import type { SignInRequest } from 'services/generated/auth-service/models/SignInRequest';
import type { TokenResponse } from 'services/generated/auth-service/models/TokenResponse';
import type { UserData } from 'services/generated/auth-service/models/UserData';
import { OpenAPI as AuthOpenApi } from 'services/generated/auth-service/core/OpenAPI';
import { WhoopAuthResourceV2Service as WhoopAuthV2 } from 'services/generated/auth-service/services/WhoopAuthResourceV2Service';
import { WhoopUsersResourceV2Service as WhoopUserV2 } from 'services/generated/auth-service/services/WhoopUsersResourceV2Service';
import * as Cookies from 'es-cookie';
import { AnalyticsBrowser } from '@segment/analytics-next';

const THIRTY_DAYS_IN_SECONDS = 2592000;
const COGNITO_AWS_REGION = 'us-west-2';

interface AuthProps {
  env: 'local' | 'dev' | 'prod';
  serverCookies?: string;
  segmentKey: string;
}

const WHOOP_AUTH_TOKEN = `whoop-auth-token`;
const WHOOP_AUTH_COOKIE_FROM_STORAGE = 'whoop.security.accessToken';
const WHOOP_REFRESH_TOKEN = `whoop-auth-refresh-token`;
const WHOOP_AUTH_EXPIRY = `whoop-auth-expiry`;
const WHOOP_AUTH_USER = `whoop-auth-user`;

// https://whoopinc.atlassian.net/browse/GRO-3512
// This is being replaced and should be removed in 3 months after
const WHOOP_LEGACY_IOS_COOKIE = `@whoopauth.accessToken`;
const WHOOP_ENCODED_LEGACY_IOS_COOKIE = `%40whoopauth.accessToken`;

const WHOOP_LEGACY_IOS_EXPIRY = `@whoopauth.accessToken.expires`;
const WHOOP_ENCODED_LEGACY_IOS_EXPIRY = `%40whoopauth.accessToken.expires`;

export const PROD_CLIENT_ID = '37365lrcda1js3fapqfe2n40eh';
export const DEV_CLIENT_ID = '5311fm7hqak9lrnpht6kp2p5uh';

interface ErrorWithMessage {
  message: string;
}

interface SegmentTrack {
  eventName: string;
  category: 'sign_in_web_page' | 'forgot_password_web_page';
  action:
    | 'invalid_signin'
    | 'sign_in'
    | 'forgot_password_page_view'
    | 'resetPasswordV2Failed'
    | 'resetPasswordV2Success';
}

interface SetCookiesParams {
  domainName?: string;
  secureSetting?: boolean;
}

interface ResetPasswordParams {
  resetToken: string;
  newPassword: string;
}

interface SendPasswordEmailParams {
  email: string;
  useV2?: boolean;
}

class ClientAuth {
  #now = new Date();
  #authenticated = false;
  #token = '';
  #tokenExpiresAt = this.#now; // instantiate to expire right away
  #refreshToken = '';
  #refreshTokenExpiresAt = this.#now; // instantiate to expire right away
  segmentAnalytics: AnalyticsBrowser | null = null;
  #apiEnv = 'prod';
  #clientId = '';
  #cognitoClient: CognitoIdentityProviderClient;
  #hasChallengeResponse = false;
  #challengeName = '';
  #session = '';
  #medium = '';
  #destination = '';

  ONE_HOUR = 60 * 60 * 1000;

  #isRefreshing = false;

  constructor({ env, segmentKey }: AuthProps) {
    const apiEnv = env === 'prod' ? 'prod' : 'dev';
    this.#apiEnv = apiEnv;
    this.#clientId = env === 'prod' ? PROD_CLIENT_ID : DEV_CLIENT_ID;

    const endpointUrl = `https://api.${
      this.#apiEnv
    }.whoop.com/auth-service/v3/whoop`;
    this.#cognitoClient = new CognitoIdentityProviderClient({
      region: COGNITO_AWS_REGION,
      endpoint: endpointUrl,
    });

    AuthOpenApi.BASE = `https://api.${apiEnv}.whoop.com/auth-service`;
    this.#cognitoClient = new CognitoIdentityProviderClient({
      region: COGNITO_AWS_REGION,
      endpoint: `https://api.${this.#apiEnv}.whoop.com/auth-service/v3/whoop`,
    });
    this.loadAuthenticationData();
    if (this.isClientSide()) {
      this.initSegment(segmentKey);
    }
  }

  getChallengeName(): string {
    return this.#challengeName;
  }
  getSession(): string {
    return this.#session;
  }
  getMedium(): string {
    return this.#medium;
  }
  getDestination(): string {
    return this.#destination;
  }

  async initSegment(segmentKey: string) {
    this.segmentAnalytics = new AnalyticsBrowser();
    this.segmentAnalytics
      .load({
        writeKey: segmentKey,
      })
      .catch((error) => {
        console.error('There was a problem initiating segment', error);
      });
  }

  isClientSide() {
    return typeof window !== 'undefined';
  }

  getToken(): string {
    return this.#token;
  }

  getRefreshToken(): string {
    return this.#refreshToken;
  }

  getUserFromCookie(): UserData | undefined {
    const user = Cookies.get(WHOOP_AUTH_USER);
    if (!user) {
      return undefined;
    }
    try {
      return JSON.parse(user);
    } catch (error) {
      return undefined;
    }
  }

  async getUser(): Promise<UserData | undefined> {
    // Avoid calling the API if we don't have a token yet
    if (!this.#token || this.#token.trim() === '') {
      console.warn('No token provided, skipping user fetch');
      return undefined;
    }
    AuthOpenApi.BASE = `https://api.${this.#apiEnv}.whoop.com/auth-service`;
    AuthOpenApi.HEADERS = {
      Authorization: `Bearer ${this.#token}`,
    };
    let userResponse;
    try {
      userResponse = await WhoopUserV2.getUser1();
    } catch (error) {
      console.error('There was a problem initiating user', error);
    }
    return userResponse?.user;
  }

  trackEvent({ eventName, category, action }: SegmentTrack) {
    try {
      this.segmentAnalytics?.track(eventName, { category, action });
    } catch (error) {
      console.error('There was a problem tracking event', error);
    }
  }

  isAuthenticated(): boolean {
    return this.#authenticated;
  }

  cleanCookie = (cookie: string) => {
    const decodedToken = decodeURIComponent(cookie)?.replaceAll('"', '');
    try {
      // only some cookies are parseable
      const parsedToken = JSON.parse(decodedToken);
      return parsedToken;
    } catch (_error) {
      // if the token is not JSON parseable, keep it as is
      return decodedToken;
    }
  };

  loadLegacyToken(): void {
    const legacyToken =
      Cookies.get(WHOOP_LEGACY_IOS_COOKIE) ??
      Cookies.get(WHOOP_ENCODED_LEGACY_IOS_COOKIE) ??
      '';
    const legacyExpiry =
      Cookies.get(WHOOP_LEGACY_IOS_EXPIRY) ??
      Cookies.get(WHOOP_ENCODED_LEGACY_IOS_EXPIRY) ??
      '';

    if (legacyToken && legacyExpiry) {
      this.#token = this.cleanCookie(legacyToken);
      this.#tokenExpiresAt = new Date(this.cleanCookie(legacyExpiry));

      // Set auth state
      this.#authenticated = true;

      // Normalize the token and expiry
      Cookies.remove(WHOOP_LEGACY_IOS_COOKIE);
      Cookies.remove(WHOOP_ENCODED_LEGACY_IOS_COOKIE);
      Cookies.set(WHOOP_AUTH_TOKEN, this.#token, {
        expires: this.#tokenExpiresAt,
        secure: true,
      });
      Cookies.remove(WHOOP_LEGACY_IOS_EXPIRY);
      Cookies.remove(WHOOP_ENCODED_LEGACY_IOS_EXPIRY);
      Cookies.set(WHOOP_AUTH_EXPIRY, this.#tokenExpiresAt.toString(), {
        expires: this.#tokenExpiresAt,
        secure: true,
      });
    }
  }

  hasChallengeResponse(): boolean {
    return this.#hasChallengeResponse;
  }

  async respondToChallenge({
    email,
    pin,
  }: {
    email: string;
    pin: string;
  }): Promise<void> {
    try {
      const response = await this.#cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: this.#challengeName as ChallengeNameType,
          Session: this.#session,
          ClientId: this.#clientId,
          ChallengeResponses: {
            USERNAME: email,
            ...(this.#challengeName === 'EMAIL_OTP'
              ? { EMAIL_OTP_CODE: pin }
              : this.#challengeName === 'SMS_MFA'
              ? { SMS_MFA_CODE: pin }
              : { EMAIL_OTP_CODE: pin }), // Default to EMAIL_OTP_CODE for other challenges
          },
        }),
      );
      if (response.AuthenticationResult) {
        const tokenData = this.normalizeCognitoResponse(
          response as InitiateAuthCommandOutput,
        );
        this.#hasChallengeResponse = false;
        this.#challengeName = '' as ChallengeNameType;
        this.#session = '';
        this.#medium = '';
        this.#destination = '';
        this.setData(tokenData);
        await this.setCookies({});
      }
    } catch (error) {
      console.error('Error while responding to challenge', error);
      throw error;
    }
  }

  async loadAuthenticationData(): Promise<void> {
    if (!this.#authenticated && this.isClientSide()) {
      const authToken =
        Cookies.get(WHOOP_AUTH_TOKEN) ||
        Cookies.get(WHOOP_AUTH_COOKIE_FROM_STORAGE);
      this.#token = authToken ? this.cleanCookie(authToken) : '';
      if (!this.#token) {
        // attempt to load legacy token
        this.loadLegacyToken();
      }
      this.#refreshToken = Cookies.get(WHOOP_REFRESH_TOKEN) ?? '';
      this.#authenticated = this.#token.length > 0;

      if (this.#refreshToken && (this.isExpired() || this.isAboutToExpire())) {
        await this.refresh();
      }
    }
  }

  isExpired(): boolean {
    return this.#token === '';
  }

  isRefreshing(): boolean {
    return this.#isRefreshing;
  }

  isAboutToExpire(): boolean {
    // if within 1 hour of expiration
    const authExpiry = Cookies.get(WHOOP_AUTH_EXPIRY);
    if (authExpiry) {
      const expiresWithinHour =
        new Date(authExpiry).valueOf() - Date.now() < this.ONE_HOUR;
      return this.isAuthenticated() && expiresWithinHour;
    }
    return false;
  }

  setData(data: TokenResponse): void {
    this.#token = data.access_token ?? '';
    this.#refreshToken = data.refresh_token ?? '';
    this.#tokenExpiresAt = new Date(
      Date.now() + (data.access_token_expires_in ?? 0) * 1000,
    );
    this.#refreshTokenExpiresAt = new Date(
      Date.now() + (data.refresh_token_expires_in ?? 0) * 1000,
    );
    if (this.#token.length > 0) {
      this.#authenticated = true;
    }
  }

  determineDomain(): { domain: string; secure: boolean } {
    const currentHostname = window.location.hostname;
    const isDevHost = currentHostname.endsWith('.dev.whoop.com');
    const allowedDomains = [
      '.whoop-web-ecomm-jf.pages.dev',
      '.whoop-web-shop.pages.dev',
      '.whoop-web-web-app.pages.dev',
      '.whoop-web-trials.pages.dev',
      '.whoop-web-ui.pages.dev',
      '.whoop.workers.dev',
    ];
    const COOKIE_SETTINGS = {
      local: {
        domain: 'localhost',
        secure: false,
        path: '/',
      },
      dev: {
        domain: '.dev.whoop.com',
        secure: true,
        path: '/',
      },
      preview: {
        domain: currentHostname,
        secure: true,
        path: '/',
      },
      prod: {
        domain: '.whoop.com',
        secure: true,
        path: '/',
      },
    };
    // Safeguard
    if (!window) return COOKIE_SETTINGS.prod;
    if (isDevHost) {
      return COOKIE_SETTINGS.dev;
    } else if (currentHostname === 'localhost') {
      return COOKIE_SETTINGS.local;
    } else if (
      allowedDomains.some((domain) => currentHostname.endsWith(domain))
    ) {
      return COOKIE_SETTINGS.preview;
    }
    return COOKIE_SETTINGS.prod;
  }

  /**
   * Check if a given hostname is a preview URL (Cloudflare Pages or Workers dev environment)
   */
  isPreviewURL(hostname: string): boolean {
    const previewDomains = ['.pages.dev', '.workers.dev'];

    return previewDomains.some((domain) => hostname.endsWith(domain));
  }

  /**
   * Low-level helper when you explicitly want to target a specific cookie domain.
   * Use sparingly; in normal flows prefer setCookiesForCurrentHost().
   */
  private setCookiesForDomain(
    domain: string,
    secure: boolean,
    userData?: string,
  ): void {
    const path = '/';

    Cookies.set(WHOOP_AUTH_TOKEN, this.#token, {
      domain,
      secure,
      expires: this.#tokenExpiresAt,
      path,
    });
    Cookies.set(WHOOP_AUTH_EXPIRY, this.#tokenExpiresAt.toString(), {
      domain,
      secure,
      expires: this.#tokenExpiresAt,
      path,
    });
    Cookies.set(WHOOP_REFRESH_TOKEN, this.#refreshToken, {
      domain,
      secure,
      expires: this.#refreshTokenExpiresAt,
      path,
    });
    if (userData) {
      Cookies.set(WHOOP_AUTH_USER, userData, {
        domain,
        secure,
        path,
      });
    }
  }

  /**
   * The single canonical writer that chooses the right parent domain for the current host.
   */
  private setCookiesForCurrentHost(userData?: string): void {
    const { domain, secure } = this.determineDomain();
    const base = {
      path: '/',
      ...(domain ? { domain } : {}),
      secure,
    } as const;

    Cookies.set(WHOOP_AUTH_TOKEN, this.#token, {
      ...base,
      expires: this.#tokenExpiresAt,
    });
    Cookies.set(WHOOP_AUTH_EXPIRY, this.#tokenExpiresAt.toString(), {
      ...base,
      expires: this.#tokenExpiresAt,
    });
    Cookies.set(WHOOP_REFRESH_TOKEN, this.#refreshToken, {
      ...base,
      expires: this.#refreshTokenExpiresAt,
    });
    if (userData) {
      Cookies.set(WHOOP_AUTH_USER, userData, base);
    }
  }

  async setCookies({
    domainName,
    secureSetting,
  }: SetCookiesParams = {}): Promise<void> {
    // Get user data once if authenticated
    let userData: string | undefined;
    if (this.#authenticated) {
      const user = await this.getUser();
      userData = JSON.stringify(user);
    }

    // If specific domain requested, just use that (escape hatch)
    if (domainName !== undefined && secureSetting !== undefined) {
      this.setCookiesForDomain(domainName, secureSetting, userData);
      return;
    }

    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : '';

    // For preview URLs (.pages.dev, .workers.dev), set cookies only for the specific hostname
    if (this.isPreviewURL(hostname)) {
      // Set cookies only for the specific preview hostname
      this.setCookiesForDomain(hostname, true, userData);
    } else {
      // Normal path: set cookies for current host's parent scope
      // PR:   .whoop.workers.dev
      // Dev:  .dev.whoop.com (incl. local.dev.whoop.com)
      // Prod: .whoop.com
      // Localhost: host-only
      this.setCookiesForCurrentHost(userData);
    }
  }

  clearCookies(): void {
    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : '';
    const { domain, secure } = this.determineDomain();

    const removeCookies = (domain?: string, secure?: boolean) => {
      const options = {
        path: '/',
        ...(domain ? { domain } : {}),
        ...(typeof secure === 'boolean' ? { secure } : {}),
      };
      Cookies.remove(WHOOP_AUTH_TOKEN, options);
      Cookies.remove(WHOOP_REFRESH_TOKEN, options);
      Cookies.remove(WHOOP_AUTH_EXPIRY, options);
      Cookies.remove(WHOOP_AUTH_USER, options);
    };

    // For preview URLs (.pages.dev, .workers.dev), clear only the preview domain
    if (this.isPreviewURL(hostname)) {
      // Clear cookies only for the specific preview hostname
      removeCookies(hostname, true);
    } else {
      // For all other domains, just clear for the determined domain
      removeCookies(domain, secure);
    }

    this.#token = '';
    this.#refreshToken = '';
    this.#authenticated = false;
  }

  trackSignInPageView(): void {
    this.segmentAnalytics?.track('Auth Flow', {
      category: 'sign_in_web_page',
      action: 'viewed',
    });
  }

  async signIn({ username, password }: SignInRequest): Promise<void> {
    this.segmentAnalytics?.track('Auth Flow', {
      category: 'sign_in_web_page',
      action: 'sign_in',
    });
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: this.#clientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });
      const response = await this.#cognitoClient.send(command);
      if (response.ChallengeName) {
        this.#hasChallengeResponse = true;
        this.#challengeName = response.ChallengeName;
        this.#session = response.Session ?? '';
        this.#medium =
          response.ChallengeParameters?.CODE_DELIVERY_DELIVERY_MEDIUM ?? '';
        this.#destination =
          response.ChallengeParameters?.CODE_DELIVERY_DESTINATION ?? '';
      } else {
        const tokenData = this.normalizeCognitoResponse(response);
        this.setData(tokenData);
        await this.setCookies({});
      }
    } catch (error) {
      if (error instanceof NotAuthorizedException) {
        this.segmentAnalytics?.track('Auth Flow', {
          category: 'sign_in_web_page',
          action: 'invalid_signin',
        });
        throw Error(`Failed to sign in: ${error.message}`);
      }
      if (error instanceof UserNotFoundException) {
        this.segmentAnalytics?.track('Auth Flow', {
          category: 'sign_in_web_page',
          action: 'invalid_signin',
        });
        throw Error(`Failed to sign in: ${error.message}`);
      }
      console.error('Error while signing in', error);
    }
  }

  isAccessTokenCognito(): boolean {
    // Cognito access tokens are JWT tokens, so they have 3 parts separated by dots
    return this.#token.split('.').length === 3;
  }

  async refreshWithWhoop(): Promise<void> {
    try {
      this.#isRefreshing = true;
      const data = await WhoopAuthV2.refresh({
        authorization: `Bearer ${this.#refreshToken}`,
      });
      this.setData(data);
      await this.setCookies({});
    } catch (_error) {
      const error = _error as ErrorWithMessage;
      if (error?.message) {
        if (error.message.includes('401')) {
          // If refreshing fails with 401, sign the user out
          this.clearCookies();
        } else {
          console.error('Error while refreshing token', error);
        }
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  normalizeCognitoResponse(response: InitiateAuthCommandOutput): TokenResponse {
    if (!response.AuthenticationResult) {
      throw new Error('No AuthenticationResult in response');
    }
    return {
      access_token: response.AuthenticationResult.AccessToken,
      access_token_expires_in: response.AuthenticationResult.ExpiresIn,
      refresh_token: response.AuthenticationResult.RefreshToken,
      refresh_token_expires_in: THIRTY_DAYS_IN_SECONDS,
    };
  }

  async refreshWithCognito(): Promise<void> {
    this.#isRefreshing = true;
    const input = {
      AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
      AuthParameters: {
        REFRESH_TOKEN: this.#refreshToken,
      },
      ClientId: this.#clientId,
    };

    try {
      const command = new InitiateAuthCommand(input);
      const response = await this.#cognitoClient.send(command);
      if (!response.AuthenticationResult) {
        throw new Error('No AuthenticationResult in response');
      }
      // No need to check for challenge response as we are refreshing and MFA does not get triggered here
      const tokenData = this.normalizeCognitoResponse(response);
      this.setData(tokenData);
      await this.setCookies({});
    } catch (error) {
      if (
        error instanceof NotAuthorizedException ||
        (error as any)?.name === 'NotAuthorizedException'
      ) {
        this.clearCookies();
      } else {
        console.error('Error while refreshing token', error);
      }
    } finally {
      this.#isRefreshing = false;
    }
  }

  async refresh(): Promise<void> {
    if (!this.#refreshToken) return;
    this.isAccessTokenCognito()
      ? await this.refreshWithCognito()
      : await this.refreshWithWhoop();
  }

  async signOut(): Promise<void> {
    this.isAccessTokenCognito()
      ? await this.signOutWithCognito()
      : await this.signOutWithWhoop();
  }

  async signOutWithWhoop(): Promise<void> {
    // Retain the refresh token for the API call
    const refreshToken = this.#refreshToken;
    // An intent to sign-out clears all local data irrespective of whether the API call succeeds or not
    this.clearCookies();
    const input = {
      Token: refreshToken,
      ClientId: this.#clientId,
    };
    if (refreshToken) {
      try {
        const command = new RevokeTokenCommand(input);
        await this.#cognitoClient.send(command);
      } catch (error) {
        console.error('Error while signing out', error);
        if (error instanceof InternalErrorException) {
          // retry the command one more time and do not throw an error
          const command = new RevokeTokenCommand(input);
          await this.#cognitoClient.send(command);
        }
      }
    }
  }

  async signOutWithCognito(): Promise<void> {
    // Retain the access token for the API call
    const token = this.#refreshToken;
    this.clearCookies();
    if (token) {
      try {
        const input = {
          Token: token,
          ClientId: this.#clientId,
        };
        const command = new RevokeTokenCommand(input);
        await this.#cognitoClient.send(command);
      } catch (error) {
        console.error('There was a problem signing out', error);
      }
    }
  }

  async resetPassword({
    resetToken,
    newPassword,
  }: ResetPasswordParams): Promise<void> {
    const body: ResetPasswordRequest = {
      new_password: newPassword,
    };
    await WhoopAuthV2.resetPassword1({
      authorization: `Bearer ${resetToken}`,
      requestBody: body,
    });
  }

  async sendPasswordEmail({
    email,
    useV2 = false,
  }: SendPasswordEmailParams): Promise<void> {
    await WhoopAuthV2.sendResetPasswordEmail({
      requestBody: { email },
      // eslint-disable-next-line id-length
      v2: useV2,
    });
  }

  async lockout(token: string): Promise<string> {
    try {
      this.segmentAnalytics?.track('Auth Flow', {
        category: 'lockout',
        action: 'lockout_initiated',
      });
      const location = await WhoopAuthV2.lockout({
        requestBody: {
          token,
        },
      });

      if (!location) {
        this.segmentAnalytics?.track('Auth Flow', {
          category: 'lockout',
          action: 'lockout_failed',
        });
        throw new Error('No Location header in response');
      }

      return location;
    } catch (error) {
      this.segmentAnalytics?.track('Auth Flow', {
        category: 'lockout',
        action: 'lockout_failed',
      });
      console.error('Error during lockout:', error);
      throw error;
    }
  }

  async isValidSession(): Promise<boolean> {
    if (!this.#authenticated) return false;
    try {
      const user = await this.getUser();
      return Boolean(user);
    } catch (error) {
      console.error('Error during session validation:', error);
      return false;
    }
  }

  async refreshAndValidateSession(): Promise<boolean> {
    if (!this.getRefreshToken()) return false;
    await this.refresh();
    return this.isValidSession();
  }
}

let clientInstance: ClientAuth | null = null;

const createAuth = ({ env, segmentKey }: AuthProps) => {
  // Check if an instance already exists
  if (!clientInstance) {
    // If not, create a new instance and store it in authInstance
    clientInstance = new ClientAuth({ env, segmentKey });
  }
  // Return the existing or new instance
  return clientInstance;
};

const resetInstance = () => {
  clientInstance = null;
};

export {
  createAuth as createClientAuth,
  resetInstance,
  type AuthProps,
  WHOOP_AUTH_TOKEN,
  WHOOP_AUTH_USER,
  WHOOP_REFRESH_TOKEN,
  WHOOP_AUTH_EXPIRY,
  WHOOP_LEGACY_IOS_COOKIE,
  WHOOP_LEGACY_IOS_EXPIRY,
  WHOOP_ENCODED_LEGACY_IOS_COOKIE,
};
